import React, { useEffect, useMemo, useState, useRef } from "react";
import { auth, db } from "../firebase";
import { collection, onSnapshot } from "firebase/firestore";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
  PieChart,
  Pie,
  Cell
} from "recharts";

/* ================= CUSTOM FILTERED ACCOUNT SELECTOR ================= */
const AccountSelector = ({ accounts, selectedId, onSelect, styles }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("Prop Firm"); 
  const [activeBroker, setActiveBroker] = useState("All");
  const [activePhase, setActivePhase] = useState("All");

  const selectedAcc = accounts.find(a => a.id === selectedId);

  const filteredList = useMemo(() => {
    return accounts.filter(acc => {
      const isBreached = acc.isBreached === true;
      if (activeTab === "Breached") return isBreached;
      if (isBreached) return false; 
      const isProp = acc.accountType !== 'personal';
      if (activeTab === "Prop Firm" && !isProp) return false;
      if (activeTab === "Personal" && isProp) return false;
      if (activeBroker !== "All" && (acc.name || "Unknown Broker") !== activeBroker) return false;
      if (activePhase !== "All" && (acc.phase || "Unknown") !== activePhase) return false;
      return true;
    });
  }, [accounts, activeTab, activeBroker, activePhase]);

  const { brokers, phases } = useMemo(() => {
    const categoryAccs = accounts.filter(acc => {
      const isBreached = acc.isBreached === true;
      if (activeTab === "Breached") return isBreached;
      if (isBreached) return false;
      return activeTab === "Prop Firm" ? acc.accountType !== 'personal' : acc.accountType === 'personal';
    });
    const brokerList = ["All", ...new Set(categoryAccs.map(a => a.name || "Unknown Broker").filter(Boolean))];
    const currentBrokerAccs = activeBroker === "All" 
        ? categoryAccs 
        : categoryAccs.filter(a => (a.name || "Unknown Broker") === activeBroker);
    const phaseList = ["All", ...new Set(currentBrokerAccs.map(a => a.phase).filter(Boolean))];
    return { brokers: brokerList, phases: phaseList };
  }, [accounts, activeTab, activeBroker]);

  const formatLabel = (acc) => {
    if (!acc) return { name: "Select Account", id: "No ID", size: "$0", phase: "Personal" };
    return {
      name: acc.name || "Unknown Broker",
      id: acc.accountCode || "No ID",
      size: `${acc.currency || "$"}${Number(acc.size || 0).toLocaleString()}`,
      phase: acc.phase || "Unknown Phase"
    };
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div onClick={() => setIsOpen(!isOpen)} style={styles.customSelectTrigger}>
        {selectedAcc ? (
          <div style={styles.selectTextWrapper}>
            <span style={{ color: '#fff', fontWeight: '700' }}>{formatLabel(selectedAcc).name}</span>
            <span style={styles.separator}>|</span>
            <span style={{ color: '#94a3b8' }}>{formatLabel(selectedAcc).id}</span>
            <span style={styles.separator}>|</span>
            <span style={{ color: '#22c55e', fontWeight: '600' }}>{formatLabel(selectedAcc).size}</span>
            <span style={styles.separator}>|</span>
            <span style={styles.phaseBadge(formatLabel(selectedAcc).phase)}>{formatLabel(selectedAcc).phase}</span>
          </div>
        ) : "Select Account"}
        <span style={{ fontSize: '10px', transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0)', color: '#475569' }}>▼</span>
      </div>
      {isOpen && (
        <>
          <div style={styles.dropdownOverlay} onClick={() => setIsOpen(false)} />
          <div style={{ ...styles.dropdownMenu, width: '450px', padding: '12px', maxHeight: 'none' }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', borderBottom: '1px solid #1e293b', paddingBottom: '12px' }}>
              {["Prop Firm", "Personal", "Breached"].map(tab => (
                <button key={tab} onClick={() => { setActiveTab(tab); setActiveBroker("All"); setActivePhase("All"); }} style={{ padding: '8px 12px', borderRadius: '6px', fontSize: '10px', fontWeight: '800', cursor: 'pointer', border: 'none', transition: 'all 0.2s', backgroundColor: activeTab === tab ? '#3b82f6' : '#1e293b', color: activeTab === tab ? '#fff' : '#94a3b8', letterSpacing: '0.5px' }}>
                  {tab.toUpperCase()}
                </button>
              ))}
            </div>
            {brokers.length > 1 && (
              <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '8px' }}>
                {brokers.map(b => (
                  <span key={b} onClick={() => { setActiveBroker(b); setActivePhase("All"); }} style={{ padding: '5px 12px', borderRadius: '20px', fontSize: '10px', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: '600', border: activeBroker === b ? '1px solid #3b82f6' : '1px solid #1e293b', background: activeBroker === b ? 'rgba(59, 130, 246, 0.1)' : '#080b11', color: activeBroker === b ? '#3b82f6' : '#64748b' }}>{b}</span>
                ))}
              </div>
            )}
            {phases.length > 1 && (
              <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '12px', marginBottom: '8px', borderBottom: '1px solid #1e293b' }}>
                {phases.map(p => (
                  <span key={p} onClick={() => setActivePhase(p)} style={{ padding: '4px 10px', borderRadius: '4px', fontSize: '9px', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: '800', textTransform: 'uppercase', border: activePhase === p ? '1px solid #22c55e' : '1px solid #1e293b', background: activePhase === p ? 'rgba(34, 197, 94, 0.1)' : '#080b11', color: activePhase === p ? '#22c55e' : '#475569' }}>{p}</span>
                ))}
              </div>
            )}
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {filteredList.length > 0 ? filteredList.map(acc => {
                const label = formatLabel(acc);
                return (
                  <div key={acc.id} onClick={() => { onSelect(acc.id); setIsOpen(false); }} style={{ ...styles.dropdownOption, backgroundColor: selectedId === acc.id ? '#1e293b' : 'transparent', borderRadius: '6px', margin: '2px 0', borderBottom: 'none' }}>
                    <div style={styles.selectTextWrapper}>
                      <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <span style={{ fontWeight: '700', fontSize: '13px', color: '#fff' }}>{label.name}</span>
                        <span style={{ fontSize: '10px', color: '#64748b' }}>{label.id}</span>
                      </div>
                      <span style={{ fontWeight: '700', color: '#22c55e', fontSize: '12px', marginRight: '8px' }}>{label.size}</span>
                      <span style={styles.phaseBadge(label.phase)}>{label.phase}</span>
                    </div>
                  </div>
                );
              }) : <div style={{ padding: '30px', textAlign: 'center', color: '#475569', fontSize: '12px' }}>No accounts found in this section.</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default function AccountDashboard() {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [trades, setTrades] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedDateStr, setSelectedDateStr] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const dashboardRef = useRef(null);

  const PIE_COLORS = ['#3b82f6', '#22c55e', '#facc15', '#ef4444', '#a855f7', '#64748b'];

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      html, body, #root { background-color: #080b11 !important; margin: 0; padding: 0; min-height: 100vh; }
      ::-webkit-scrollbar { width: 8px; height: 8px; }
      ::-webkit-scrollbar-track { background: #080b11; }
      ::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 10px; }
      ::-webkit-scrollbar-thumb:hover { background: #334155; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    if (!auth.currentUser) return;
    const ref = collection(db, "users", auth.currentUser.uid, "accounts");
    return onSnapshot(ref, snap => {
      const accs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAccounts(accs);
      if (!selectedAccountId && accs.length) setSelectedAccountId(accs[0].id);
    });
  }, [selectedAccountId]);

  useEffect(() => {
    if (!auth.currentUser || !selectedAccountId) return;
    const ref = collection(db, "users", auth.currentUser.uid, "accounts", selectedAccountId, "trades");
    return onSnapshot(ref, snap => setTrades(snap.docs.map(d => d.data())));
  }, [selectedAccountId]);

  useEffect(() => {
    if (!auth.currentUser || !selectedAccountId) return;
    const ref = collection(db, "users", auth.currentUser.uid, "accounts", selectedAccountId, "withdrawals");
    return onSnapshot(ref, snap => setWithdrawals(snap.docs.map(d => d.data())));
  }, [selectedAccountId]);

  useEffect(() => {
    if (selectedAccountId && !fromDate && !toDate) applyPreset("mtd");
  }, [selectedAccountId]);

  const account = useMemo(() => accounts.find(a => a.id === selectedAccountId), [accounts, selectedAccountId]);

  const applyPreset = type => {
    const today = new Date();
    const fmt = d => d.toISOString().split("T")[0];
    if (type === "today") { setFromDate(fmt(today)); setToDate(fmt(today)); }
    if (type === "7d") { const from = new Date(today); from.setDate(today.getDate() - 6); setFromDate(fmt(from)); setToDate(fmt(today)); }
    if (type === "30d") { const from = new Date(today); from.setDate(today.getDate() - 29); setFromDate(fmt(from)); setToDate(fmt(today)); }
    if (type === "mtd") { const from = new Date(today.getFullYear(), today.getMonth(), 1); setFromDate(fmt(from)); setToDate(fmt(today)); }
  };

  const filteredTrades = useMemo(() => {
    if (!fromDate && !toDate) return trades;
    return trades.filter(t => {
      if (!t.date) return false;
      const d = new Date(t.date);
      if (fromDate && d < new Date(fromDate)) return false;
      if (toDate && d > new Date(toDate)) return false;
      return true;
    });
  }, [trades, fromDate, toDate]);

  const handleExportPDF = async () => {
    if (!dashboardRef.current || !account) return;
    setIsExporting(true);
    
    try {
      const element = dashboardRef.current;
      
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#080b11",
        logging: false,
        scrollY: -window.scrollY, 
        width: element.scrollWidth,
        height: element.scrollHeight
      });

      const imgData = canvas.toDataURL("image/png");
      
      const imgWidth = 210; 
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      const pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: [imgWidth, imgHeight]
      });

      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      
      const fileName = `${account.name || 'Account'}_Export.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const lastTenTrades = useMemo(() => [...trades].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 10), [trades]);
  const tradePnL = useMemo(() => filteredTrades.reduce((sum, t) => sum + Number(t.pnl || 0), 0), [filteredTrades]);
  
  // WITHDRAWAL LOGIC: Treat withdrawal values as negative deductions
  const withdrawalStats = useMemo(() => ({ 
    count: withdrawals.length, 
    total: withdrawals.reduce((sum, w) => sum + Number(w.amount || 0), 0) 
  }), [withdrawals]);
  
  const stats = useMemo(() => {
    if (!account) return null;
    const startingBalance = Number(account.size || 0);
    // Equity Calculation: Add (negative) withdrawalStats.total to subtract from balance
    const equity = startingBalance + tradePnL + withdrawalStats.total;
    return { startingBalance, netPnL: tradePnL, equity };
  }, [account, tradePnL, withdrawalStats]);

  const limits = useMemo(() => {
    if (!account || !stats) return null;
    const size = account.size;
    const staticDailyMax = (size * account.maxDailyDD) / 100;
    const staticTotalMax = (size * account.maxTotalDD) / 100;
    if (account.ddType === "trailing") {
      const peakEquity = Math.max(account.peakEquity || size, stats.equity);
      const breachLevel = peakEquity - staticTotalMax;
      return {
        daily: { equity: stats.equity, breach: stats.equity - staticDailyMax, max: staticDailyMax },
        total: { equity: stats.equity, breach: breachLevel, max: staticTotalMax }
      };
    }
    return {
      daily: { equity: stats.equity, breach: stats.equity - staticDailyMax, max: staticDailyMax },
      total: { equity: stats.equity, breach: size - staticTotalMax, max: staticTotalMax }
    };
  }, [account, stats]);

  const risk = useMemo(() => {
    if (!stats) return null;
    const diff = stats.equity - stats.startingBalance;
    const pct = Math.abs((diff / stats.startingBalance) * 100);
    if (diff >= 0) return { label: "SAFE", color: "#22c55e", pct, mode: "gain" };
    if (pct >= 70) return { label: "CRITICAL", color: "#ef4444", pct, mode: "drawdown" };
    return { label: "WARNING", color: "#facc15", pct, mode: "drawdown" };
  }, [stats]);

  const chartData = useMemo(() => {
    if (!stats) return [];
    if (!filteredTrades.length) return [{ date: "Start", balance: stats.startingBalance, equity: stats.startingBalance }];
    const sortedTrades = [...filteredTrades].sort((a, b) => new Date(a.date) - new Date(b.date));
    const data = [];
    let runningEquity = stats.startingBalance;
    sortedTrades.forEach(t => {
      runningEquity += Number(t.pnl || 0);
      data.push({ date: t.date, balance: stats.startingBalance, equity: runningEquity });
    });
    return data;
  }, [filteredTrades, stats]);

  const insights = useMemo(() => {
    if (!filteredTrades.length) return null;
    const instrumentMap = {};
    const sessionMap = {};
    let wins = 0, losses = 0;
    filteredTrades.forEach(t => {
      const inst = t.instrument || "Unknown";
      const sess = t.session || "Unknown";
      const isWin = t.result?.toLowerCase() === "profit" || Number(t.pnl) > 0;
      const isLoss = t.result?.toLowerCase() === "loss" || Number(t.pnl) < 0;
      if (!instrumentMap[inst]) instrumentMap[inst] = { wins: 0, losses: 0 };
      if (isWin) { instrumentMap[inst].wins++; wins++; } else if (isLoss) { instrumentMap[inst].losses++; losses++; }
      if (!sessionMap[sess]) sessionMap[sess] = { wins: 0, total: 0 };
      sessionMap[sess].total++;
      if (isWin) sessionMap[sess].wins++;
    });
    const totalCalculated = wins + losses;
    return {
      instruments: Object.entries(instrumentMap).map(([name, v]) => ({ name, ...v, total: v.wins + v.losses })).sort((a, b) => b.total - a.total).slice(0, 3),
      sessions: Object.entries(sessionMap).map(([name, v]) => ({ name, winRate: v.total ? (v.wins / v.total) * 100 : 0 })),
      profitability: { wins, losses, total: totalCalculated, winRate: totalCalculated ? (wins / totalCalculated) * 100 : 0 }
    };
  }, [filteredTrades]);

  const pieInsights = useMemo(() => {
    const data = { winMindset: {}, lossMindset: {}, winMistakes: {}, lossMistakes: {} };
    filteredTrades.forEach(t => {
      const isWin = t.result?.toLowerCase() === "profit" || Number(t.pnl) > 0;
      const mindset = t.mindset || t.mindsetState || "Unknown";
      const mistake = t.mistake || t.mentalMistake || "None";
      if (isWin) {
        data.winMindset[mindset] = (data.winMindset[mindset] || 0) + 1;
        data.winMistakes[mistake] = (data.winMistakes[mistake] || 0) + 1;
      } else {
        data.lossMindset[mindset] = (data.lossMindset[mindset] || 0) + 1;
        data.lossMistakes[mistake] = (data.lossMistakes[mistake] || 0) + 1;
      }
    });
    const formatter = (obj) => Object.entries(obj).map(([name, value]) => ({ name, value }));
    return {
      winMindset: formatter(data.winMindset),
      lossMindset: formatter(data.lossMindset),
      winMistakes: formatter(data.winMistakes),
      lossMistakes: formatter(data.lossMistakes)
    };
  }, [filteredTrades]);

  const calendarDays = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const dailyPnL = {};
    trades.forEach(t => { if (t.date) dailyPnL[t.date] = (dailyPnL[t.date] || 0) + Number(t.pnl || 0); });
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null); 
    for (let d = 1; d <= totalDays; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      days.push({ day: d, dateStr, pnl: dailyPnL[dateStr] || 0 });
    }
    return days;
  }, [calendarMonth, trades]);

  const selectedDayTrades = useMemo(() => selectedDateStr ? trades.filter(t => t.date === selectedDateStr) : [], [selectedDateStr, trades]);

  if (!account || !stats || !limits || !risk) return null;
  const currency = account.currency || "$";

  const getMindsetColor = (m) => {
    const val = String(m || "").toLowerCase();
    if (val.includes("calm") || val.includes("neutral")) return "#22c55e";
    if (val.includes("fomo") || val.includes("greedy") || val.includes("revenge")) return "#ef4444";
    if (val.includes("anxious") || val.includes("hesitant")) return "#facc15";
    return "#94a3b8";
  };

  return (
    <div style={styles.container} ref={dashboardRef}>
      {selectedImage && (
        <div style={styles.modalOverlay} onClick={() => setSelectedImage(null)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <button style={styles.closeBtn} onClick={() => setSelectedImage(null)}>✕</button>
            <img src={selectedImage} alt="Trade Chart" style={styles.largeImage} />
          </div>
        </div>
      )}

      <div style={styles.flexBetween}>
        <div>
          <h2 style={styles.pageTitle}>Account Dashboard</h2>
          <p style={styles.pageSubtitle}>Monitor real-time drawdown, equity growth, and account health.</p>
        </div>
        <button 
          onClick={handleExportPDF} 
          disabled={isExporting}
          style={{...styles.exportBtn, opacity: isExporting ? 0.5 : 1}}
        >
          {isExporting ? "⌛" : "📥"} 
          <span style={{marginLeft: '8px'}}>{isExporting ? "Exporting..." : "Export PDF"}</span>
        </button>
      </div>

      <div style={styles.filterRow}>
        <div style={styles.filterCard}>
          <div style={styles.cardHeader}><span style={styles.cardIcon}>🎯</span><label style={styles.labelSmall}>ACTIVE TRADING ACCOUNT</label></div>
          <AccountSelector accounts={accounts} selectedId={selectedAccountId} onSelect={(id) => setSelectedAccountId(id)} styles={styles} />
        </div>
        <div style={styles.filterCard}>
          <div style={styles.cardHeader}>
            <span style={styles.cardIcon}>📅</span><label style={styles.labelSmall}>DATE RANGE FILTER</label>
            <div style={styles.presetContainer}>{['Today', '7D', '30D', 'MTD'].map(p => <button key={p} onClick={() => applyPreset(p.toLowerCase())} style={styles.presetBtn}>{p}</button>)}</div>
          </div>
          <div style={styles.inputGroup}>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={styles.dateInput} />
            <span style={{ color: "#475569" }}>—</span>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={styles.dateInput} />
          </div>
        </div>
      </div>

      <div style={styles.topGrid}>
        <Card title="STARTING BALANCE" value={`${currency}${stats.startingBalance.toLocaleString()}`} />
        <Card title="REALTIME EQUITY" value={`${currency}${stats.equity.toLocaleString()}`} color={stats.equity >= stats.startingBalance ? "#22c55e" : "#ef4444"} />
        <RiskCard risk={risk} />
        <Card title="EXPECTANCY" value={`${currency}${(stats.netPnL / Math.max(1, insights?.profitability.total || 1)).toFixed(2)}`} />
        {/* WITHDRAWAL CARD FIX: Showing positive value with Math.abs */}
        <Card title="WITHDRAWALS" value={withdrawalStats.count === 0 ? "No data" : `${currency}${Math.abs(withdrawalStats.total).toLocaleString()}`} />
        <ConsistencyCard filteredTrades={filteredTrades} />
      </div>

      <div style={styles.mainGrid}>
        <div style={styles.chartCard}>
          <h3 style={styles.sectionTitle}>Account Performance</h3>
          <div style={{ height: 350, width: "100%" }}>
            <ResponsiveContainer>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="date" stroke="#475569" fontSize={12} tickMargin={10} />
                <YAxis stroke="#475569" fontSize={12} tickMargin={10} />
                <Tooltip contentStyle={styles.chartTooltip} borderStyle={{ border: 'none' }} />
                <Legend iconType="circle" />
                <Line name="Base Balance" dataKey="balance" stroke="#475569" strokeDasharray="5 5" dot={false} strokeWidth={1} />
                <Line name="Current Equity" dataKey="equity" stroke="#3b82f6" strokeWidth={3} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div style={styles.sidebar}>
          <LimitCard title="DAILY LOSS LIMIT" data={limits.daily} currency={currency} />
          <LimitCard title="MAX LOSS LIMIT" data={limits.total} currency={currency} />
          <div style={styles.miniCard}>
            <label style={styles.labelSmall}>NET PROFIT / LOSS</label>
            <div style={{ ...styles.largeVal, color: stats.netPnL >= 0 ? "#22c55e" : "#ef4444" }}>{stats.netPnL >= 0 ? "+" : ""}{currency}{stats.netPnL.toLocaleString()}</div>
            {insights && (
              <div style={{ marginTop: 20 }}>
                <div style={styles.flexBetween}>
                  <span style={{ color: "#22c55e", fontSize: 12 }}>{insights.profitability.winRate.toFixed(1)}% Won ({insights.profitability.wins})</span>
                  <span style={{ color: "#ef4444", fontSize: 12 }}>{(100 - insights.profitability.winRate).toFixed(1)}% Lost ({insights.profitability.losses})</span>
                </div>
                <div style={styles.progressTrack}><div style={{ ...styles.progressFill, width: `${insights.profitability.winRate}%`, background: "#22c55e" }} /></div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '24px' }}>
        <PieCard title="Winning Mindset" data={pieInsights.winMindset} colors={PIE_COLORS} />
        <PieCard title="Losing Mindset" data={pieInsights.lossMindset} colors={PIE_COLORS} />
        <PieCard title="Winning Mistakes" data={pieInsights.winMistakes} colors={PIE_COLORS} />
        <PieCard title="Losing Mistakes" data={pieInsights.lossMistakes} colors={PIE_COLORS} />
      </div>

      <div style={{ ...styles.chartCard, marginTop: '24px', padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          <span style={{ ...styles.labelSmall, color: '#94a3b8', fontSize: '11px', minWidth: '85px' }}>Most Traded</span>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {insights?.instruments.map((inst, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '8px', height: '16px', borderRadius: '2px', backgroundColor: idx === 0 ? '#22c55e' : idx === 1 ? '#6366f1' : '#f59e0b' }} />
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#cbd5e1' }}>{inst.name.toUpperCase()} <span style={{ color: '#64748b', fontWeight: '400', marginLeft: '2px' }}>({inst.total})</span></span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px', paddingTop: '16px', borderTop: '1px solid #1e293b' }}>
          <span style={{ ...styles.labelSmall, color: '#94a3b8', fontSize: '11px', minWidth: '85px' }}>Session Win%</span>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {insights?.sessions.map((sess, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '8px', height: '16px', borderRadius: '2px', backgroundColor: sess.winRate >= 50 ? '#22c55e' : '#ef4444' }} />
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#cbd5e1' }}>{sess.name.toUpperCase()} <span style={{ color: sess.winRate >= 50 ? '#22c55e' : '#ef4444', marginLeft: '2px' }}>{sess.winRate.toFixed(0)}%</span></span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '24px', marginTop: '24px' }}>
        <div style={{ ...styles.chartCard }}>
          <div style={styles.flexBetween}>
            <h3 style={styles.sectionTitle}>PnL Calendar</h3>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
              <button onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))} style={styles.presetBtn}>Prev</button>
              <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{calendarMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
              <button onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))} style={styles.presetBtn}>Next</button>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '10px' }}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d} style={{ textAlign: 'center', fontSize: '12px', color: '#475569', fontWeight: 'bold' }}>{d}</div>)}
            {calendarDays.map((dayObj, idx) => (
              <div key={idx} onClick={() => dayObj && setSelectedDateStr(dayObj.dateStr)} style={{ height: '100px', borderRadius: '8px', padding: '10px', cursor: dayObj ? 'pointer' : 'default', border: selectedDateStr === dayObj?.dateStr ? '2px solid #3b82f6' : dayObj ? '1px solid #1e293b' : 'none', background: dayObj ? (dayObj.pnl > 0 ? 'rgba(34, 197, 94, 0.05)' : dayObj.pnl < 0 ? 'rgba(239, 68, 68, 0.05)' : 'transparent') : 'transparent', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', transition: 'all 0.2s' }}>
                {dayObj && (
                  <>
                    <span style={{ fontSize: '14px', color: selectedDateStr === dayObj.dateStr ? '#3b82f6' : '#94a3b8', fontWeight: 'bold' }}>{dayObj.day}</span>
                    {dayObj.pnl !== 0 && <span style={{ fontSize: '18px', fontWeight: '900', color: dayObj.pnl > 0 ? '#22c55e' : '#ef4444', textAlign: 'right', lineHeight: '1' }}>{dayObj.pnl > 0 ? '+' : ''}{currency}{dayObj.pnl.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
        <div style={styles.chartCard}>
          <h3 style={styles.sectionTitle}>Trades: {selectedDateStr || 'Select a day'}</h3>
          {selectedDayTrades.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {selectedDayTrades.map((t, i) => (
                <div key={i} style={{ background: '#080b11', padding: '12px', borderRadius: '8px', borderLeft: `4px solid ${Number(t.pnl) >= 0 ? '#22c55e' : '#ef4444'}` }}>
                  <div style={styles.flexBetween}><span style={{ fontWeight: '700', fontSize: '13px' }}>{t.instrument}</span><span style={{ color: Number(t.pnl) >= 0 ? '#22c55e' : '#ef4444', fontWeight: '800', fontSize: '13px' }}>{Number(t.pnl) >= 0 ? '+' : ''}{currency}{Number(t.pnl).toLocaleString()}</span></div>
                  <div style={{ fontSize: '10px', color: '#475569', marginTop: '4px' }}>{t.session} | {t.result}</div>
                </div>
              ))}
            </div>
          ) : <p style={{ color: '#475569', fontSize: '12px', textAlign: 'center', marginTop: '40px' }}>No trades found for this date.</p>}
        </div>
      </div>

      <div style={{ ...styles.chartCard, marginTop: '24px' }}>
        <h3 style={styles.sectionTitle}>Recent Account Activity (Last 10 Trades)</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead><tr style={styles.tableHeadRow}>{['DATE','CHART','INSTRUMENT','SESSION','TF','RR','SETUP','MINDSET','MISTAKE','RATING','RESULT','PROFIT / LOSS'].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
            <tbody>
              {lastTenTrades.length > 0 ? lastTenTrades.map((t, i) => (
                <tr key={i} style={styles.tableRow}>
                  <td style={styles.td}>{t.date}</td>
                  <td style={styles.td}>{t.tradeImageUrl ? <div onClick={() => setSelectedImage(t.tradeImageUrl)} style={styles.thumbnailWrapper}><img src={t.tradeImageUrl} alt="Trade Chart" style={styles.thumbnail} /></div> : <span style={{ color: '#1e293b', fontSize: '10px' }}>NO IMAGE</span>}</td>
                  <td style={{ ...styles.td, fontWeight: '700' }}>{t.instrument}</td>
                  <td style={styles.td}>{t.session}</td>
                  <td style={styles.td}>{t.timeframe || t.tf || '—'}</td>
                  <td style={styles.td}>{t.rr || '—'}</td>
                  <td style={styles.td}>{t.setup || t.reason || '—'}</td>
                  <td style={{ ...styles.td, color: getMindsetColor(t.mindset || t.mindsetState), fontWeight: '600' }}>{t.mindset || t.mindsetState || '—'}</td>
                  <td style={{ ...styles.td, color: (t.mistake || t.mentalMistake) && (t.mistake || t.mentalMistake) !== 'None' ? '#ef4444' : '#94a3b8' }}>{t.mistake || t.mentalMistake || 'None'}</td>
                  <td style={styles.td}><span style={{ color: '#facc15', fontWeight: '700' }}>⭐ {t.tradeRating || t.rating || '0'}/10</span></td>
                  <td style={styles.td}><span style={{ color: t.result?.toLowerCase() === 'profit' ? '#22c55e' : '#ef4444', background: t.result?.toLowerCase() === 'profit' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700' }}>{t.result?.toUpperCase()}</span></td>
                  <td style={{ ...styles.td, color: Number(t.pnl) >= 0 ? '#22c55e' : '#ef4444', fontWeight: '800' }}>{Number(t.pnl) >= 0 ? '+' : ''}{currency}{Number(t.pnl).toLocaleString()}</td>
                </tr>
              )) : <tr><td colSpan="12" style={{ ...styles.td, textAlign: 'center', padding: '40px', color: '#475569' }}>No trading history found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Card = ({ title, value, color = "#fff" }) => (
  <div style={styles.metricCard}><label style={styles.labelSmall}>{title}</label><div style={{ ...styles.metricVal, color }}>{value}</div></div>
);

const RiskCard = ({ risk }) => (
  <div style={styles.metricCard}><label style={styles.labelSmall}>RISK STATUS</label><div style={{ fontSize: 20, fontWeight: "900", color: risk.color }}>{risk.label}</div>
    <div style={styles.progressTrack}><div style={{ ...styles.progressFill, width: `${Math.min(risk.pct, 100)}%`, background: risk.color }} /></div>
  </div>
);

const ConsistencyCard = ({ filteredTrades }) => {
  const dailyPnL = {};
  filteredTrades.forEach(t => { if (t.date) dailyPnL[t.date] = (dailyPnL[t.date] || 0) + Number(t.pnl || 0); });
  const profitableDays = Object.values(dailyPnL).filter(v => v > 0);
  if (profitableDays.length < 1) return <Card title="CONSISTENCY" value="N/A" />;
  const totalProfit = profitableDays.reduce((a, b) => a + b, 0);
  const maxDayProfit = Math.max(...profitableDays);
  const consistency = totalProfit > 0 ? 100 - (maxDayProfit / totalProfit) * 100 : 0;
  const color = consistency > 60 ? "#22c55e" : consistency > 40 ? "#facc15" : "#ef4444";
  return (
    <div style={styles.metricCard}><label style={styles.labelSmall}>CONSISTENCY</label><div style={{ fontSize: 20, fontWeight: "900", color }}>{consistency.toFixed(1)}%</div>
      <div style={styles.progressTrack}><div style={{ ...styles.progressFill, width: `${consistency}%`, background: color }} /></div>
    </div>
  );
};

const LimitCard = ({ title, data, currency }) => {
  const remaining = Math.max(0, data.equity - data.breach);
  const usedPct = data.max ? (Math.max(0, data.max - remaining) / data.max) * 100 : 0;
  const color = usedPct > 80 ? "#ef4444" : usedPct > 50 ? "#facc15" : "#22c55e";
  return (
    <div style={{ ...styles.miniCard, marginBottom: 16 }}>
      <label style={styles.labelSmall}>{title}</label><div style={{ fontSize: 18, fontWeight: "800" }}>{currency}{remaining.toLocaleString()} Remaining</div>
      <div style={styles.progressTrack}><div style={{ ...styles.progressFill, width: `${Math.min(usedPct, 100)}%`, background: color }} /></div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
        <span style={{ fontSize: 10, color: "#94a3b8" }}>Breach @ {currency}{data.breach.toLocaleString()}</span>
        <span style={{ fontSize: 10, color: "#94a3b8" }}>Max: {currency}{data.max.toLocaleString()}</span>
      </div>
    </div>
  );
};

const PieCard = ({ title, data, colors }) => (
  <div style={styles.chartCard}>
    <label style={{ ...styles.labelSmall, display: 'block', marginBottom: '10px', textAlign: 'center' }}>{title.toUpperCase()}</label>
    <div style={{ height: 180, width: '100%' }}>
      {data.length > 0 ? (
        <ResponsiveContainer>
          <PieChart>
            <Pie data={data} innerRadius={45} outerRadius={70} paddingAngle={5} dataKey="value">
              {data.map((entry, index) => <Cell key={`cell-${index}`} fill={colors[index % colors.length]} stroke="none" />)}
            </Pie>
            <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', fontSize: '10px' }} itemStyle={{ color: '#fff' }} />
          </PieChart>
        </ResponsiveContainer>
      ) : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', fontSize: '10px' }}>No Data</div>}
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', marginTop: '8px' }}>
      {data.slice(0, 3).map((item, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: colors[idx % colors.length] }} />
          <span style={{ fontSize: '9px', color: '#94a3b8' }}>{item.name}</span>
        </div>
      ))}
    </div>
  </div>
);

const styles = {
  container: { padding: "30px 40px", color: "#fff", background: "transparent", minHeight: "100vh" },
  pageTitle: { fontSize: "28px", fontWeight: "800", marginBottom: "4px" },
  pageSubtitle: { fontSize: "14px", color: "#64748b", marginBottom: "32px" },
  filterRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "32px" },
  filterCard: { background: "#0d1117", border: "1px solid #1e293b", borderRadius: "12px", padding: "20px" },
  cardHeader: { display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" },
  cardIcon: { fontSize: "16px" },
  labelSmall: { fontSize: "10px", fontWeight: "900", color: "#475569", letterSpacing: "1px" },
  presetContainer: { display: "flex", gap: "6px", marginLeft: "auto" },
  presetBtn: { background: "#1e293b", border: "none", color: "#94a3b8", fontSize: "10px", padding: "4px 10px", borderRadius: "4px", cursor: "pointer", fontWeight: "700" },
  inputGroup: { display: "flex", alignItems: "center", gap: "12px" },
  dateInput: { background: "#080b11", border: "1px solid #1e293b", color: "#fff", padding: "8px 12px", borderRadius: "8px", fontSize: "13px", outline: "none", flex: 1 },
  topGrid: { display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "16px", marginBottom: "24px" },
  metricCard: { background: "#0d1117", border: "1px solid #1e293b", borderRadius: "12px", padding: "16px" },
  metricVal: { fontSize: "20px", fontWeight: "900", marginTop: "12px" },
  mainGrid: { display: "grid", gridTemplateColumns: "1fr 350px", gap: "24px" },
  chartCard: { background: "#0d1117", border: "1px solid #1e293b", borderRadius: "12px", padding: "24px" },
  sectionTitle: { fontSize: "16px", fontWeight: "800", marginBottom: "24px", color: "#f8fafc" },
  chartTooltip: { background: "#0f172a", border: "1px solid #1e293b", borderRadius: "8px", color: "#fff" },
  sidebar: { display: "flex", flexDirection: "column" },
  miniCard: { background: "#0d1117", border: "1px solid #1e293b", borderRadius: "12px", padding: "20px" },
  largeVal: { fontSize: "28px", fontWeight: "900", marginTop: "10px" },
  progressTrack: { height: "6px", background: "#1e293b", borderRadius: "10px", marginTop: "12px", overflow: "hidden" },
  progressFill: { height: "100%", borderRadius: "10px" },
  flexBetween: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  modalOverlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.9)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center" },
  modalContent: { position: "relative", maxWidth: "90%", maxHeight: "90%" },
  largeImage: { width: "100%", height: "auto", borderRadius: "8px" },
  closeBtn: { position: "absolute", top: "-40px", right: "0", background: "transparent", border: "none", color: "#fff", fontSize: "20px", cursor: "pointer" },
  customSelectTrigger: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#080b11', border: '1px solid #1e293b', borderRadius: '8px', cursor: 'pointer' },
  selectTextWrapper: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' },
  separator: { color: '#1e293b' },
  dropdownMenu: { position: 'absolute', top: '105%', left: 0, background: '#0d1117', border: '1px solid #334155', borderRadius: '12px', zIndex: 1000, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)', overflow: 'hidden' },
  dropdownOption: { padding: '12px', cursor: 'pointer', transition: 'background 0.2s' },
  dropdownOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 },
  phaseBadge: (phase) => ({ fontSize: '8px', padding: '2px 6px', borderRadius: '4px', fontWeight: '900', background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', border: '1px solid rgba(59, 130, 246, 0.2)', textTransform: 'uppercase' }),
  table: { width: '100%', borderCollapse: 'collapse', marginTop: '10px' },
  tableHeadRow: { borderBottom: '1px solid #1e293b', textAlign: 'left' },
  th: { padding: '12px 8px', color: '#475569', fontSize: '10px', fontWeight: '900', letterSpacing: '1px' },
  tableRow: { borderBottom: '1px solid #0f172a', transition: 'background 0.2s' },
  td: { padding: '16px 8px', fontSize: '12px', color: '#cbd5e1' },
  thumbnailWrapper: { width: '40px', height: '24px', borderRadius: '4px', overflow: 'hidden', border: '1px solid #1e293b', cursor: 'pointer' },
  thumbnail: { width: '100%', height: '100%', objectFit: 'cover' },
  exportBtn: {
    background: '#3b82f6',
    border: 'none',
    color: '#fff',
    padding: '8px 16px',
    borderRadius: '8px',
    fontWeight: '800',
    fontSize: '11px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.2s',
    boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)'
  }
};